import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TaskEditData, TaskNote } from '../../hooks/useCurrentTasks';
import { TASK_TYPES, BILLING_CODES } from '../../constants/timeTrackerConstants';
import { formatDuration } from '../../utils/timeUtils';
import { IntranetAccount } from '../../types';
import './TaskEditModal.css';

// Simple project type for the edit modal
interface ProjectOption {
  id: string;
  name: string;
  accountId: string;
}

// Status options matching the intranet
const STATUS_OPTIONS = [
  { value: 'Y', label: 'Closed' },
  { value: 'N', label: 'Open' },
  { value: 'F', label: 'Flagged' },
  { value: 'R', label: 'Resolved' },
  { value: 'B', label: 'Billed' },
] as const;

// Estimated time options
const ESTIMATED_TIME_OPTIONS = [
  { value: '5', label: '5 Minutes' },
  { value: '10', label: '10 Minutes' },
  { value: '15', label: '15 Minutes' },
  { value: '30', label: '30 Minutes' },
  { value: '60', label: '1 Hour' },
  { value: '120', label: '2 Hours' },
  { value: '180', label: '3 Hours' },
  { value: '240', label: '4 Hours' },
  { value: '300', label: '5 Hours' },
  { value: '360', label: '6 Hours' },
  { value: '420', label: '7 Hours' },
  { value: '480', label: '8 Hours' },
  { value: '540', label: '9 Hours' },
  { value: '600', label: '10 Hours' },
  { value: '', label: 'Unknown' },
] as const;

interface TaskEditModalProps {
  task: TaskEditData | null;
  isOpen: boolean;
  isLoading: boolean;
  isSaving: boolean;
  accounts: IntranetAccount[];
  projects: ProjectOption[];
  onClose: () => void;
  onSave: (taskData: TaskEditData) => Promise<void>;
}

/**
 * Parse a date string (various formats) to a Date object
 * Handles: YYYY-MM-DD, MM/DD/YYYY, -0001-11-30 (invalid date placeholder)
 */
const parseDate = (dateStr: string | undefined | null): Date | null => {
  if (!dateStr || dateStr.startsWith('-0001') || dateStr === '0000-00-00') {
    return null;
  }
  
  // Try parsing as YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) return date;
  }
  
  // Try parsing as MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(dateStr)) {
    const [month, day, year] = dateStr.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime())) return date;
  }
  
  return null;
};

/**
 * Format a Date object to MM/DD/YYYY string for the form
 */
const formatDateForForm = (date: Date | null): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const TaskEditModal: React.FC<TaskEditModalProps> = ({
  task,
  isOpen,
  isLoading,
  isSaving,
  accounts,
  projects,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState<TaskEditData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when task changes
  useEffect(() => {
    if (task) {
      setFormData({
        ...task,
        // Coerce IDs to strings so <select> option values match reliably
        accountId: String(task.accountId ?? ''),
        projectId: String(task.projectId ?? ''),
      });
      setError(null);
    }
  }, [task]);

  if (!isOpen) return null;

  const handleChange = (field: keyof TaskEditData, value: string) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    setError(null);
    try {
      // The intranet's edit form submits a `company` field formatted as
      // "accountId - accountName". Send it to match the browser form exactly.
      const selectedAccount = accounts.find((a) => String(a.id) === String(formData.accountId));
      const company = selectedAccount
        ? `${selectedAccount.id} - ${selectedAccount.name}`
        : formData.company || '';
      await onSave({ ...formData, company });
    } catch (err: any) {
      setError(err.message || 'Failed to update task');
    }
  };

  const showWhyField = formData?.type === 'FIX' || formData?.type === 'FALLOUT';
  const showFlaggedWhy = formData?.completed === 'F' || formData?.completed === 'R' || formData?.completed === 'B';
  const showBillingCode = formData?.type === 'UPDATE';
  // project_id is optional on the intranet; only show the field for task types
  // that normally use a project.
  const showProjectField = formData?.type === 'PROJECT' || formData?.type === 'FALLOUT';

  return (
    <div className="task-edit-modal-overlay" onClick={onClose}>
      <div className="task-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Update Task</h3>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>
            ×
          </button>
        </div>

        {isLoading ? (
          <div className="modal-loading">
            <div className="loading-spinner"></div>
            <span>Loading task details...</span>
          </div>
        ) : !formData ? (
          <div className="modal-error">
            <span>Failed to load task details</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="form-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {/* Account */}
              <div className="form-group">
                <label className="form-label">Account</label>
                <select
                  className="form-select"
                  value={String(formData.accountId ?? '')}
                  onChange={(e) => {
                    // Update account and clear project in one setState so the
                    // new accountId isn't overwritten by a stale form snapshot.
                    const accountId = e.target.value;
                    setFormData((prev) =>
                      prev ? { ...prev, accountId, projectId: '' } : prev
                    );
                  }}
                  disabled={isSaving}
                >
                  <option value="">- Select Account -</option>
                  {accounts
                    .filter((account) => !/^\d+$/.test(account.name)) // Filter out numeric-only names
                    .map((account) => (
                    <option key={account.id} value={String(account.id)}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project - only shown for PROJECT and FALLOUT task types */}
              {showProjectField && (
                <div className="form-group">
                  <label className="form-label">Project</label>
                  <select
                    className="form-select"
                    value={String(formData.projectId ?? '')}
                    onChange={(e) => handleChange('projectId', e.target.value)}
                    disabled={isSaving || !formData.accountId}
                  >
                    <option value="">- Select Project -</option>
                    {projects
                      .filter((p) => String(p.accountId) === String(formData.accountId))
                      .map((project) => (
                        <option key={project.id} value={String(project.id)}>
                          {project.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              {/* Status */}
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={formData.completed}
                  onChange={(e) => handleChange('completed', e.target.value)}
                  disabled={isSaving}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Task Type */}
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="">- Task Type -</option>
                  {TASK_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  disabled={isSaving}
                  rows={3}
                  placeholder="Task description..."
                />
              </div>

              {/* Billing Code - shown for UPDATE */}
              {showBillingCode && (
                <div className="form-group">
                  <label className="form-label">Billing Code</label>
                  <select
                    className="form-select"
                    value={formData.codeId}
                    onChange={(e) => handleChange('codeId', e.target.value)}
                    disabled={isSaving}
                  >
                    <option value="">-- UNDEFINED --</option>
                    {BILLING_CODES.map((code) => (
                      <option key={code.value} value={code.value}>
                        {code.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Why Fix/Fallout */}
              {showWhyField && (
                <div className="form-group">
                  <label className="form-label why-label">Why a Fix/Fallout?</label>
                  <textarea
                    className="form-textarea why-textarea"
                    value={formData.descriptionWhy}
                    onChange={(e) => handleChange('descriptionWhy', e.target.value)}
                    disabled={isSaving}
                    rows={2}
                    placeholder="Explain why this is a fix or fallout..."
                  />
                </div>
              )}

              {/* Why Flagged */}
              {showFlaggedWhy && (
                <div className="form-group">
                  <label className="form-label flagged-label">Why Flagged?</label>
                  <textarea
                    className="form-textarea flagged-textarea"
                    value={formData.flaggedWhy}
                    onChange={(e) => handleChange('flaggedWhy', e.target.value)}
                    disabled={isSaving}
                    rows={2}
                    placeholder="Explain why this task is flagged..."
                  />
                </div>
              )}

              {/* Time Fields Row */}
              <div className="form-row">
                {/* Time Logged (minutes and seconds, stored as total seconds) */}
                <div className="form-group form-group-half">
                  <label className="form-label">Time Logged</label>
                  <div className="time-input-wrapper time-dual-input">
                    <div className="time-unit-group">
                      <input
                        type="number"
                        className="form-input time-input"
                        value={Math.floor(parseInt(formData.timeTotal) / 60) || 0}
                        onChange={(e) => {
                          const minutes = parseInt(e.target.value) || 0;
                          const currentSeconds = (parseInt(formData.timeTotal) || 0) % 60;
                          handleChange('timeTotal', String(minutes * 60 + currentSeconds));
                        }}
                        disabled={isSaving}
                        min="0"
                        step="1"
                      />
                      <span className="time-unit-label">min</span>
                    </div>
                    <span className="time-separator-colon">:</span>
                    <div className="time-unit-group">
                      <input
                        type="number"
                        className="form-input time-input time-input-seconds"
                        value={(parseInt(formData.timeTotal) || 0) % 60}
                        onChange={(e) => {
                          let seconds = parseInt(e.target.value) || 0;
                          // Clamp seconds between 0 and 59
                          seconds = Math.max(0, Math.min(59, seconds));
                          const currentMinutes = Math.floor((parseInt(formData.timeTotal) || 0) / 60);
                          handleChange('timeTotal', String(currentMinutes * 60 + seconds));
                        }}
                        disabled={isSaving}
                        min="0"
                        max="59"
                        step="1"
                      />
                      <span className="time-unit-label">sec</span>
                    </div>
                  </div>
                  <span className="time-display-hint">
                    Total: {formatDuration(parseInt(formData.timeTotal) || 0)}
                  </span>
                </div>

                {/* Estimated Time */}
                <div className="form-group form-group-half">
                  <label className="form-label">Estimated Time</label>
                  <select
                    className="form-select"
                    value={formData.timeEst}
                    onChange={(e) => handleChange('timeEst', e.target.value)}
                    disabled={isSaving}
                  >
                    {ESTIMATED_TIME_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date Fields Row */}
              <div className="form-row">
                {/* Date Closed */}
                <div className="form-group form-group-half">
                  <label className="form-label">Date Closed</label>
                  <DatePicker
                    selected={parseDate(formData.dateClosed)}
                    onChange={(date: Date | null) => handleChange('dateClosed', formatDateForForm(date))}
                    dateFormat="MM/dd/yyyy"
                    className="form-input date-picker-input"
                    placeholderText="MM/DD/YYYY"
                    disabled={isSaving}
                    isClearable
                  />
                </div>

                {/* Due Date */}
                <div className="form-group form-group-half">
                  <label className="form-label">Due Date</label>
                  <DatePicker
                    selected={parseDate(formData.dateDue)}
                    onChange={(date: Date | null) => handleChange('dateDue', formatDateForForm(date))}
                    dateFormat="MM/dd/yyyy"
                    className="form-input date-picker-input"
                    placeholderText="MM/DD/YYYY"
                    disabled={isSaving}
                    isClearable
                  />
                </div>
              </div>

              {/* Notes Section */}
              <div className="form-group">
                <label className="form-label">Notes</label>
                {formData.notes && formData.notes.length > 0 && (
                  <div className="notes-container">
                    <div className="notes-list">
                      {formData.notes.map((note: TaskNote) => (
                        <div key={note.id} className="note-item">
                          <div className="note-header">
                            <span className="note-author">{note.author}</span>
                            {note.timestamp && (
                              <span className="note-timestamp">{note.timestamp}</span>
                            )}
                          </div>
                          <div className="note-text">{note.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="add-note-container">
                  <label className="add-note-label">
                    {formData.notes && formData.notes.length > 0 ? 'Add a note' : 'Add a note'}
                  </label>
                  <textarea
                    className="form-textarea add-note-textarea"
                    value={formData.newNote || ''}
                    onChange={(e) => handleChange('newNote', e.target.value)}
                    disabled={isSaving}
                    rows={3}
                    placeholder="Enter a new note..."
                  />
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="modal-btn modal-btn-save"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <div className="btn-spinner"></div>
                    Saving...
                  </>
                ) : (
                  'Update Task'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default TaskEditModal;

